import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { Loading } from "@/components/shared/Loading";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useSupplierInvoices, useAction, useJobDocuments } from "@/api/hooks";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle, Banknote, Copy, FileText, Loader2, ChevronDown, ChevronUp, Truck, Sparkles, ScrollText } from "lucide-react";

const ACTION_LOCKED_STATUSES = new Set([
  "APPROVED",
  "PAID",
  "REJECTED",
  "REJECTED (DUPLICATE)",
  "SENT FOR APPROVAL",
]);

const statusKey = (value) => String(value || "").trim().toUpperCase().replaceAll("_", " ");
const today = () => new Date().toISOString().slice(0, 10);

// Item 11 — Finance must see ALL original documents, neatly SECTIONED
// (trade docs from the customer vs cost/booking evidence from vendors —
// same category split as item 8's Operations view), before approving
// anything. Expand-on-demand so the list stays scannable by default.
function DocumentsExpander({ jobId, onOpenDoc }) {
  const [open, setOpen] = useState(false);
  const { data: docs } = useJobDocuments(open ? jobId : null);
  if (!jobId) return null;
  const trade = (docs || []).filter((d) => d.category !== "COST_EVIDENCE");
  const cost = (docs || []).filter((d) => d.category === "COST_EVIDENCE");
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`ap-doc-expander-${jobId}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[12px] font-medium text-indigo-600 inline-flex items-center gap-1">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {open ? "Hide" : "View"} all original documents for {jobId}
      </button>
      {open && (
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3 text-violet-500" />Trade documents · {trade.length}</div>
            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {trade.length ? trade.map((d) => (
                <button key={d.id} onClick={() => onOpenDoc(d)} className="w-full text-left px-2.5 py-1.5 flex items-center gap-2 hover:bg-slate-50 text-[12.5px]">
                  <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span className="truncate flex-1">{d.name}</span><StatusPill status={d.review} />
                </button>
              )) : <div className="px-2.5 py-1.5 text-[12px] text-slate-400">None on file</div>}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Truck className="h-3 w-3 text-indigo-500" />Cost & booking evidence · {cost.length}</div>
            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {cost.length ? cost.map((d) => (
                <button key={d.id} onClick={() => onOpenDoc(d)} className="w-full text-left px-2.5 py-1.5 flex items-center gap-2 hover:bg-slate-50 text-[12.5px]">
                  <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span className="truncate flex-1">{d.name}</span><StatusPill status={d.review} />
                </button>
              )) : <div className="px-2.5 py-1.5 text-[12px] text-slate-400">None on file</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FinancePayables() {
  const { data: rows, error } = useSupplierInvoices();
  const act = useAction(["ap"]);
  const qc = useQueryClient();
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [payment, setPayment] = useState({ reference: "", paidAt: today() });
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [varianceBusy, setVarianceBusy] = useState(null);
  const [accrualOpen, setAccrualOpen] = useState(false);
  const [accrualDraft, setAccrualDraft] = useState({ jobId: "", vendor: "", amount: "", currency: "AED", note: "" });
  const [accrualBusy, setAccrualBusy] = useState(false);
  const openDoc = (d) => { setActiveDoc(d); setDocOpen(true); };

  if (error) return <div className="p-6 text-slate-500">Supplier invoices are restricted for your role.</div>;
  if (!rows) return <Loading />;

  const run = (invoice, action, msg) => act.mutate(
    {
      url: `/finance/supplier-invoices/${invoice.id}/action`,
      body: { action, expectedVersion: Number(invoice.version ?? 0) },
    },
    {
      onSuccess: () => toast.success(msg),
      onError: (e) => toast.error(apiErrorMessage(e, "This invoice could not be updated.")),
    },
  );

  // Item 11 gap-fill: this used to call the generic `escalate` action, which
  // only flipped a status string with no real approval record behind it.
  // Now calls the actual APR-xxxx-creating endpoint from the AP gap-fill
  // pass (`services/approvals.py` policy registry), so a variance can never
  // be approved without a real, valid SUPPLIER_VARIANCE approval.
  const requestVarianceApproval = async (invoice) => {
    setVarianceBusy(invoice.id);
    try {
      await api.post(`/finance/supplier-invoices/${invoice.id}/request-variance-approval`, { expectedVersion: Number(invoice.version ?? 0) });
      await qc.invalidateQueries({ queryKey: ["ap"] });
      toast.success("Variance approval requested — a real APR record was created");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not request variance approval")); }
    finally { setVarianceBusy(null); }
  };

  const reverseAccrual = async (invoice) => {
    try {
      await api.post(`/finance/supplier-invoices/${invoice.id}/reverse-accrual`, { expectedVersion: Number(invoice.version ?? 0), reason: "Actual supplier invoice recorded" });
      await qc.invalidateQueries({ queryKey: ["ap"] });
      toast.success("Accrual reversed");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not reverse accrual")); }
  };

  const submitAccrual = async (event) => {
    event.preventDefault();
    if (!accrualDraft.jobId.trim() || !accrualDraft.amount) return;
    setAccrualBusy(true);
    try {
      await api.post(`/finance/jobs/${accrualDraft.jobId.trim()}/accrual`, {
        vendor: accrualDraft.vendor.trim() || undefined, amount: Number(accrualDraft.amount),
        currency: accrualDraft.currency, note: accrualDraft.note || undefined,
      });
      await qc.invalidateQueries({ queryKey: ["ap"] });
      toast.success("Accrual recorded — job cost is now closure-eligible pending the real invoice");
      setAccrualOpen(false);
      setAccrualDraft({ jobId: "", vendor: "", amount: "", currency: "AED", note: "" });
    } catch (e) { toast.error(apiErrorMessage(e, "Could not record accrual")); }
    finally { setAccrualBusy(false); }
  };

  const openPayment = (invoice) => {
    setPaymentInvoice(invoice);
    setPayment({ reference: "", paidAt: today() });
  };

  const recordPayment = async (event) => {
    event.preventDefault();
    if (!paymentInvoice || !payment.reference.trim() || !payment.paidAt) return;
    setPaymentBusy(true);
    try {
      await api.post(`/finance/supplier-invoices/${paymentInvoice.id}/payment`, {
        reference: payment.reference.trim(),
        paidAt: payment.paidAt,
        expectedVersion: Number(paymentInvoice.version ?? 0),
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["ap"] }),
        qc.invalidateQueries({ queryKey: ["profit"] }),
      ]);
      toast.success("Supplier payment recorded");
      setPaymentInvoice(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Supplier payment could not be recorded."));
    } finally {
      setPaymentBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Supplier Invoices" subtitle="Invoice matching, approval and supplier payment control"
        actions={<Button size="sm" variant="outline" className="h-8" onClick={() => setAccrualOpen(true)} data-testid="ap-record-accrual-btn"><ScrollText className="h-3.5 w-3.5 mr-1.5" />Record accrual</Button>} />
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {rows.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            No supplier invoices are awaiting processing.
          </div>
        )}
        {rows.map((s) => {
          const workflowStatus = s.status || "Pending review";
          const workflowKey = statusKey(workflowStatus);
          const flag = s.match !== "Match";
          const canTakeReviewAction = !ACTION_LOCKED_STATUSES.has(workflowKey);
          const canRecordPayment = workflowKey === "APPROVED";

          return (
            <div key={s.id} data-testid={`ap-${s.id}`} className={`rounded-lg border bg-white p-4 ${flag ? "border-amber-200" : "border-slate-200"}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${s.match === "Duplicate" ? "bg-red-50" : s.match === "Variance" ? "bg-amber-50" : "bg-emerald-50"}`}>
                    {s.match === "Duplicate" ? <Copy className="h-4 w-4 text-red-500" /> : s.match === "Variance" ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <span className="text-emerald-600 text-lg">✓</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{s.vendor}</div>
                    <div className="text-xs text-slate-500 font-mono-num">{s.id} · Job {s.jobId} · {s.note}</div>
                    {s.source === "operations_handoff" && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">
                        <Truck className="h-3 w-3" />Submitted by Operations · {s.submittedBy || "—"}
                      </div>
                    )}
                    {s.isAccrual && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                        <ScrollText className="h-3 w-3" />Estimated accrual
                      </div>
                    )}
                    {s.businessStage && (
                      <div className="mt-2 flex min-w-0 items-center gap-2">
                        <BusinessStage stage={s.businessStage} compact />
                        <span className="truncate text-[11px] text-slate-500">{s.businessStage.name} · Last status: {s.businessStage.lastStatus}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="text-right"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Invoiced</div><div className="font-mono-num font-semibold text-slate-900">{money(s.amount, s.currency || "AED")}</div></div>
                  <div className="text-right"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Expected</div><div className="font-mono-num text-slate-600">{s.expected == null ? "Not captured" : money(s.expected, s.currency || "AED")}</div></div>
                  <div className="min-w-[132px]"><div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Workflow status</div><StatusPill status={workflowStatus} /></div>
                  <div className="min-w-[92px]"><div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Match</div><StatusPill status={s.match} /></div>
                </div>
              </div>

              {(s.documents || []).length > 0 && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`ap-docs-${s.id}`}>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Attached ({s.documents.length})</span>
                  {s.documents.map((d) => (
                    <span key={d} data-testid={`ap-doc-${s.id}-${d}`} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600"><FileText className="h-3 w-3 text-blue-700" />{d}</span>
                  ))}
                  <span className="text-xs text-slate-500">Review before approving</span>
                </div>
              )}

              <DocumentsExpander jobId={s.jobId} onOpenDoc={openDoc} />

              {(canTakeReviewAction || canRecordPayment || (s.isAccrual && workflowKey !== "REVERSED")) && (
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                  {s.isAccrual && workflowKey !== "REVERSED" && (
                    <Button size="sm" variant="outline" onClick={() => reverseAccrual(s)} data-testid={`ap-reverse-accrual-${s.id}`}>Reverse accrual</Button>
                  )}
                  {canRecordPayment ? (
                    <Button size="sm" onClick={() => openPayment(s)} data-testid={`ap-payment-${s.id}`}><Banknote className="mr-1.5 h-4 w-4" />Record payment</Button>
                  ) : s.match === "Duplicate" ? (
                    <Button size="sm" variant="destructive" disabled={act.isPending} onClick={() => run(s, "reject", "Marked as duplicate and excluded from payment")} data-testid={`ap-dup-${s.id}`}>Reject duplicate</Button>
                  ) : s.match === "Variance" ? (
                    <>
                      <Button size="sm" variant="outline" disabled={varianceBusy === s.id} onClick={() => requestVarianceApproval(s)} data-testid={`ap-review-${s.id}`}>
                        {varianceBusy === s.id && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Request variance approval
                      </Button>
                      <Button size="sm" disabled={act.isPending} onClick={() => run(s, "approve", "Approved with note and booked to the job")} data-testid={`ap-approve-${s.id}`}>Approve with note</Button>
                    </>
                  ) : (
                    <Button size="sm" disabled={act.isPending} onClick={() => run(s, "approve", "Supplier invoice approved and matched to the job")} data-testid={`ap-approve-${s.id}`}>Approve and match</Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!paymentInvoice} onOpenChange={(open) => { if (!open && !paymentBusy) setPaymentInvoice(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="ap-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Banknote className="h-4 w-4 text-blue-700" />Record supplier payment</DialogTitle>
            <DialogDescription>{paymentInvoice ? `${paymentInvoice.vendor} · ${paymentInvoice.id} · ${money(paymentInvoice.amount, paymentInvoice.currency || "AED")}` : ""}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={recordPayment}>
            <div>
              <label htmlFor="ap-payment-reference" className="text-xs font-semibold text-slate-700">Payment reference</label>
              <Input id="ap-payment-reference" value={payment.reference} onChange={(e) => setPayment((v) => ({ ...v, reference: e.target.value }))} placeholder="Bank transfer or transaction reference" className="mt-1" required autoFocus />
            </div>
            <div>
              <label htmlFor="ap-payment-date" className="text-xs font-semibold text-slate-700">Paid date</label>
              <Input id="ap-payment-date" type="date" value={payment.paidAt} onChange={(e) => setPayment((v) => ({ ...v, paidAt: e.target.value }))} className="mt-1" required />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setPaymentInvoice(null)} disabled={paymentBusy}>Cancel</Button>
              <Button type="submit" disabled={paymentBusy || !payment.reference.trim() || !payment.paidAt} data-testid="ap-payment-submit">
                {paymentBusy && <Loader2 className="h-4 w-4 animate-spin" />}Record payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={accrualOpen} onOpenChange={(open) => { if (!open && !accrualBusy) setAccrualOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="ap-accrual-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ScrollText className="h-4 w-4 text-blue-700" />Record cost accrual</DialogTitle>
            <DialogDescription>For a Job that is operationally complete (POD verified) but the real supplier bill hasn't arrived yet — keeps closure/GP review from being blocked or wrong.</DialogDescription>
          </DialogHeader>
          <form className="space-y-3" onSubmit={submitAccrual}>
            <div><Label>Job ID *</Label><Input value={accrualDraft.jobId} onChange={(e) => setAccrualDraft((v) => ({ ...v, jobId: e.target.value }))} placeholder="e.g. J-2026-0142" required autoFocus className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Estimated amount *</Label><Input type="number" min="0.01" step="0.01" value={accrualDraft.amount} onChange={(e) => setAccrualDraft((v) => ({ ...v, amount: e.target.value }))} required className="mt-1" /></div>
              <div><Label>Currency</Label><Input value={accrualDraft.currency} onChange={(e) => setAccrualDraft((v) => ({ ...v, currency: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Vendor (optional)</Label><Input value={accrualDraft.vendor} onChange={(e) => setAccrualDraft((v) => ({ ...v, vendor: e.target.value }))} className="mt-1" /></div>
            <div><Label>Note</Label><Textarea rows={2} value={accrualDraft.note} onChange={(e) => setAccrualDraft((v) => ({ ...v, note: e.target.value }))} className="mt-1" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAccrualOpen(false)} disabled={accrualBusy}>Cancel</Button>
              <Button type="submit" disabled={accrualBusy || !accrualDraft.jobId.trim() || !accrualDraft.amount} data-testid="ap-accrual-submit">
                {accrualBusy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Record accrual
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DocumentPanel doc={activeDoc} open={docOpen} onOpenChange={setDocOpen} />
    </>
  );
}
